/* eslint-disable */
import React from "react";
import clsx from "clsx";
import download from "downloadjs";
import { toast } from "react-toastify";

import Modal from "../../../../../components/ui/Modal";
import { MailIcon, MobileIcon, SuccessIcon } from "../../../../../assets/Icons";
import ErrorField from "../../../../../components/ui/ErrorField";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { remittanceActions } from "../../../state/remittanceSlice";
import { AppStrings, logApiErrorToSentry } from "../../../utils";
import { buildDigioPayload } from "../../../utils/buildDigioPayload";
import { isDigioSigningComplete, normalizeDigioRecord } from "../../../utils/digioStatus";
import { digioApi, parseDigioRecord } from "../../../../../services/digio";
import { K2DigioRecord } from "../../../types/digio/K2Digio";
import { styles } from "./styles";
import { InlineSpinner } from "./InlineSpinner";

type LoadingAction = "sms" | "email" | "doLater" | "resend" | null;

export interface DigioEsignModalProps {
  visible?: boolean;
  onClose: () => void;
  modalTitle?: string;
  onDownloadA2Form?: () => void;
}

export const DigioEsignModal: React.FC<DigioEsignModalProps> = ({
  visible,
  onClose,
  modalTitle,
  onDownloadA2Form,
}) => {
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);
  const { calculator, remitter, recipient, review } = useAppSelector((state) => state.remittance);

  const [mobileActive, setMobileActive] = React.useState(false);
  const [mailActive, setMailActive] = React.useState(false);
  const [step, setStep] = React.useState("1");
  const [error, setError] = React.useState("");
  const [loadingAction, setLoadingAction] = React.useState<LoadingAction>(null);
  const [isTimeUp, setIsTimeUp] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState(60);
  const [clickedTime, setClickedTime] = React.useState(0);

  const remitterUserKey = remitter?.selectedRemitter?.userKey ?? "";
  const loggedInUserKey = authState?.currentUser?.vendor_key ?? authState?.userKey ?? "";
  const digioRecord = review?.digioEsign?.data;
  const isResumeFlow = digioRecord?.status === "DO_LATER";
  const isLoading = loadingAction !== null;

  const resetSelections = () => {
    setMobileActive(false);
    setMailActive(false);
    setLoadingAction(null);
  };

  const updateDigioRecord = (record: K2DigioRecord | null) => {
    const evaluated = record ? normalizeDigioRecord(record) : null;
    dispatch(remittanceActions.setDigioEsignData(evaluated));
    return evaluated;
  };

  const startTimer = () => {
    clickedTime < 2 ? setTimeLeft(60) : timeLeft === 0 && setTimeLeft(60);
    setIsTimeUp(false);
    setClickedTime((prev) => prev + 1);
  };

  const syncStepFromRecord = (record?: K2DigioRecord | null) => {
    if (isDigioSigningComplete(record?.status)) {
      setStep("3");
      return;
    }

    if (record?.status === "AWAITING_SIGN") {
      setStep("2");
      setMobileActive(record?.sendType === "sms");
      setMailActive(record?.sendType === "email");
      return;
    }

    setStep("1");
    if (record?.status !== "DO_LATER") {
      setMobileActive(false);
      setMailActive(false);
    }
  };

  React.useEffect(() => {
    if (!visible) {
      setError("");
      setLoadingAction(null);
      return;
    }

    syncStepFromRecord(digioRecord);

    const uniqueKey = digioRecord?.uniqueKey;
    if (!uniqueKey) {
      return;
    }

    digioApi
      .getStatus(uniqueKey)
      .then((res) => {
        const record = updateDigioRecord(parseDigioRecord(res));
        syncStepFromRecord(record);
      })
      .catch((err) => {
        logApiErrorToSentry(err, "digioGetStatusOnOpen");
      });
  }, [visible]);

  React.useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    const uniqueKey = digioRecord?.uniqueKey;

    if (visible && step === "2" && uniqueKey) {
      interval = setInterval(() => {
        digioApi
          .getStatus(uniqueKey)
          .then((res) => {
            const record = updateDigioRecord(parseDigioRecord(res));
            if (isDigioSigningComplete(record?.status)) {
              setStep("3");
            }
          })
          .catch((err) => {
            setError(err?.response?.data?.error?.type || AppStrings.GENERIC_ERROR);
            logApiErrorToSentry(err, "digioGetStatus");
          });
      }, 3000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [visible, step, digioRecord?.uniqueKey]);

  React.useEffect(() => {
    if (timeLeft && clickedTime >= 2) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => (prev <= 1 ? (setIsTimeUp(true), 0) : prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft, clickedTime]);

  const sendLink = async (type: "sms" | "email") => {
    if (!remitterUserKey || !loggedInUserKey || isLoading) {
      return;
    }

    startTimer();
    type === "sms" ? setMobileActive(true) : setMailActive(true);
    setLoadingAction(type);
    setError("");

    const payload = buildDigioPayload({
      remitterUserKey,
      loggedInUserKey,
      selectedRemitter: remitter?.selectedRemitter,
      selectedRecipient: recipient?.selectedRecipient,
      amountCalculation: calculator?.amountCalculation,
      transactionSummary: calculator?.getTransactionSummary?.data,
      sendType: type,
      sourceOfFunds: review?.reviewDetails?.sourceOfFundsDropdown?.label ?? "",
    });

    try {
      let response;
      if (isResumeFlow && digioRecord?.uniqueKey) {
        response = await digioApi.resume(digioRecord.uniqueKey, payload);
      } else {
        response = await digioApi.uploadDocument(payload);
      }

      updateDigioRecord(parseDigioRecord(response));
      setStep("2");
    } catch (err: any) {
      setError(err?.response?.data?.error?.type || AppStrings.GENERIC_ERROR);
      type === "sms" ? setMobileActive(false) : setMailActive(false);
      logApiErrorToSentry(err, isResumeFlow ? "digioResume" : "digioUploadDocument");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDoLaterA2Form = async () => {
    if (!remitterUserKey || !loggedInUserKey || isLoading) {
      return;
    }

    setLoadingAction("doLater");
    setError("");

    try {
      const response = await digioApi.doLater({
        userKey: remitterUserKey,
        loggedInUserKey,
        requestType: "k2",
        purpose_code: calculator?.amountCalculation?.purposeCode ?? "",
        purpose_description: calculator?.amountCalculation?.transaction_purpose ?? "",
      });
      updateDigioRecord(parseDigioRecord(response));
      onClose();
      resetSelections();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.type || AppStrings.GENERIC_ERROR);
      logApiErrorToSentry(err, "digioDoLater");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownloadSignedA2 = async () => {
    const documentId = digioRecord?.digioDocId;
    if (!documentId) {
      onDownloadA2Form?.();
      return;
    }

    try {
      const response = await digioApi.downloadDocument(documentId);
      const content = response.headers["content-type"];
      download(response.data, "signed_A2_form.pdf", content);
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.type || AppStrings.GENERIC_ERROR);
      logApiErrorToSentry(err, "digioDownloadDocument");
    }
  };

  const handleResendLink = async () => {
    const uniqueKey = digioRecord?.uniqueKey;
    if (!uniqueKey || isLoading || (clickedTime > 2 && !isTimeUp)) {
      return;
    }

    startTimer();
    setLoadingAction("resend");
    setError("");

    try {
      const response = await digioApi.resendLink(uniqueKey);
      const record = updateDigioRecord(parseDigioRecord(response));
      syncStepFromRecord(record);
    } catch (err: any) {
      setError(err?.response?.data?.error?.type || AppStrings.GENERIC_ERROR);
      logApiErrorToSentry(err, "digioResendLink");
    } finally {
      setLoadingAction(null);
    }
  };

  const formatTime = (sec: number) =>
    `${String(Math.floor(sec / 60)).padStart(2, "0")}.${String(sec % 60).padStart(2, "0")}`;

  const renderOptionButton = (
    type: "sms" | "email",
    icon: React.ReactNode,
    label: string,
    isActive: boolean
  ) => (
    <button
      type="button"
      onClick={() => sendLink(type)}
      disabled={isLoading}
      className={clsx(
        styles.optionButton,
        isActive ? styles.optionButtonActive : styles.optionButtonDefault
      )}
    >
      {icon}
      <span className={styles.optionLabel}>{label}</span>
      {loadingAction === type && (
        <InlineSpinner className={isActive ? "text-white" : "text-primary"} />
      )}
    </button>
  );

  return (
    <Modal
      modalClass={styles.modalClass}
      headerClass={styles.headerClass}
      visible={visible}
      header={modalTitle || AppStrings.REMITTER_ESIGN_MODAL.SIGN_A2_FORM}
      closeHandler={() => {
        onClose();
        resetSelections();
      }}
    >
      {step === "1" && (
        <div className={styles.receiveLinkWrapper}>
          <h1 className={styles.receiveLinkTitle}>
            {isResumeFlow
              ? AppStrings.DIGIO_ESIGN_MODAL.RESUME_MESSAGE
              : AppStrings.REMITTER_ESIGN_MODAL.RECEIVE_LINK_ON}
          </h1>

          {(mobileActive || mailActive) && (
            <span className={styles.linkActiveNote}>
              {AppStrings.REMITTER_ESIGN_MODAL.LINK_ACTIVE_FOR_5_MIN}
            </span>
          )}

          <div className={styles.optionsRow}>
            {renderOptionButton(
              "sms",
              <MobileIcon />,
              AppStrings.REMITTER_ESIGN_MODAL.MOBILE,
              mobileActive
            )}
            {renderOptionButton(
              "email",
              <MailIcon />,
              AppStrings.REMITTER_ESIGN_MODAL.EMAIL,
              mailActive
            )}
          </div>

          {(mobileActive || mailActive) && (
            <span className={styles.linkSentMessage}>
              {mobileActive
                ? AppStrings.DIGIO_ESIGN_MODAL.LINK_SENT_MSG
                : AppStrings.DIGIO_ESIGN_MODAL.LINK_SENT_EMAIL}
            </span>
          )}

          {!isResumeFlow && (
            <button
              type="button"
              onClick={handleDoLaterA2Form}
              disabled={isLoading}
              className={styles.doItLaterButton}
            >
              <span>{AppStrings.REMITTER_ESIGN_MODAL.DO_IT_LATER}</span>
              {loadingAction === "doLater" && <InlineSpinner className="text-primary" />}
            </button>
          )}

          {error && (
            <div className={styles.errorWrapper}>
              <ErrorField errorMessage={error} />
            </div>
          )}
        </div>
      )}

      {step === "2" && (
        <div className={styles.awaitingWrapper}>
          <div className="mb-8 max-w-md">
            <h1 className={styles.awaitingTitle}>
              {mobileActive
                ? AppStrings.DIGIO_ESIGN_MODAL.LINK_SENT_MSG
                : AppStrings.DIGIO_ESIGN_MODAL.LINK_SENT_EMAIL}
            </h1>
          </div>

          <div className="mb-8">
            <h1 className={styles.awaitingNote}>{AppStrings.DIGIO_ESIGN_MODAL.NOTE_REVIEW_A2}</h1>
          </div>

          <div className="mb-4">
            <h1 className={styles.awaitingNote}>{AppStrings.DIGIO_ESIGN_MODAL.AWAITING_ESIGN}</h1>
          </div>

          <div className={styles.dotsWrapper}>
            <div className={styles.dot}></div>
            <div className={styles.dot}></div>
            <div className={styles.dot}></div>
          </div>

          {clickedTime > 2 && !isTimeUp && (
            <span className="font-semibold text-[12.2px] text-center mb-4">
              {formatTime(timeLeft)}
            </span>
          )}

          <button
            type="button"
            onClick={handleResendLink}
            disabled={(clickedTime > 2 && !isTimeUp) || isLoading}
            className={styles.resendBtn}
          >
            <span>{AppStrings.DIGIO_ESIGN_MODAL.RESEND_LINK}</span>
            {loadingAction === "resend" && <InlineSpinner className="text-white" />}
          </button>
        </div>
      )}

      {step === "3" && (
        <div className={styles.signedWrapper}>
          <h1 className={styles.signedTitle}>{AppStrings.DIGIO_ESIGN_MODAL.ESIGN_DONE}</h1>
          <h1 className={styles.signedSubtitle}>{AppStrings.REMITTER_ESIGN_MODAL.THANKS}</h1>

          <SuccessIcon className="mb-5" />

          <button type="button" onClick={handleDownloadSignedA2} className={styles.signedLink}>
            {AppStrings.REMITTER_ESIGN_MODAL.DOWNLOAD_A2}
          </button>

          <button
            type="button"
            onClick={() => {
              onClose();
              setStep("1");
            }}
            className={styles.signedLink}
          >
            {AppStrings.REMITTER_ESIGN_MODAL.GO_BACK}
          </button>
        </div>
      )}
    </Modal>
  );
};
